/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { useState } from "react";
import { IoMdClose } from "react-icons/io";
import { useRecoilState, useRecoilValue } from "recoil";
import { CampaignActionAtom } from "../../../../atoms/CampaignActionAtom";
import { CampaingBuilderFormAtom } from "../../../../atoms/CampaingBuilderFormAtom";
import useMediaQuery from "../../../../hook/useMediaQuery";
import Warning from "../../../Modal/container/Warning";
import { useModal } from "../../../Modal/Modal";

interface ContainerProps {
  selected: boolean;
  error: boolean;
  isFormOpen?: boolean;
}

const Title = styled.div`
  color: #2f2f2f;
  font-size: 16px;
  font-weight: 600;

  display: flex;
  align-items: center;
  justify-content: center;

  > svg {
    margin-right: 4px;
  }
`;

const Container = styled(motion.div)<ContainerProps>`
  width: ${(props) => (props.isFormOpen ? "82px" : "240px")};
  min-height: ${(props) => (props.isFormOpen ? "82px" : "fit-content")};
  background: #fff;
  padding: 16px;
  border-radius: 4px;
  border: 1px solid #dddddd;
  display: flex;
  position: relative;
  flex-direction: column;
  align-items: center;
  justify-content: ${(props) => (props.isFormOpen ? "center" : "start")};
  cursor: pointer;

  img {
    width: ${(props) => (props.isFormOpen ? "40px" : "16px")};
    height: ${(props) => (props.isFormOpen ? "40px" : "16px")};
  }

  ${(props) =>
    props.selected &&
    css`
      box-shadow: 0px 0px 6px #fb972e4d;
      border: 1px solid #fb972e;
    `}
  ${(props) =>
    props.error &&
    css`
      box-shadow: 0px 0px 6px #ff0000;
      border: 1px solid #ff0000;
    `}

    @media screen and (min-width: 1080px) {
    width: 240px;
    min-height: fit-content;
    justify-content: start;

    img {
      width: 16px;
      height: 16px;
    }
  }
`;

const TagsWrapper = styled.div`
  width: calc(100% + 32px);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  grid-gap: 8px;
  margin-top: 8px;
  margin-bottom: -16px;
  padding-bottom: 8px;
`;

interface IconButtonProps {
  isDelete?: boolean;
  isFormOpen?: boolean;
}

const IconButton = styled.div<IconButtonProps>`
  height: 24px;
  width: 24px;
  position: absolute;
  top: 12px;
  right: 6px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: opacity 0.2s;
  pointer-events: ${(props) => (props.isDelete ? "default" : "none")};
  opacity: ${(props) => (props.isDelete ? 1 : 0)};

  @media screen and (max-width: 1079px) {
    height: ${(props) => (props.isFormOpen ? "20px" : "24px")};
    width: ${(props) => (props.isFormOpen ? "20px" : "24px")};
    top: ${(props) => (props.isFormOpen ? "2px" : "12px")};
    right: ${(props) => (props.isFormOpen ? "4px" : "6px")};
  }
`;

interface StopCard {
  onClick?: () => void;
  onDelete?: (e: any) => void;
  description: string | JSX.Element;
  selected: boolean;
  error: boolean;
  original: boolean;
}

export default function StopCard({
  onClick,
  onDelete,
  description,
  selected,
  error,
  original,
}: StopCard) {
  const [form, setForm] = useRecoilState(CampaingBuilderFormAtom);
  const action = useRecoilValue(CampaignActionAtom);
  const [isHover, setIsHover] = useState<boolean>(false);
  const modal = useModal();
  const isLargerThan1080 = useMediaQuery("(min-width: 1080px)");

  return (
    <Container
      isFormOpen={form !== null}
      onClick={onClick}
      selected={selected}
      error={error}
      onHoverStart={() => setIsHover(true)}
      onHoverEnd={() => setIsHover(false)}
    >
      <IconButton
        isFormOpen={form !== null}
        isDelete={isHover}
        onClick={(node: any) => {
          if (action === "create" && original) {
            modal.pushModal(
              <Warning
                buttonsName={["No", "Yes"]}
                title="Delete campaign node?"
                description="Are you sure you want to permanently delete this campaign node?"
                icon={"./icons/delete.svg"}
                onOkClicked={() => {
                  onDelete(node);
                  modal.popModal();
                }}
                onCancelClicked={() => modal.popModal()}
              />
            );
          } else {
            onDelete(node);
          }
        }}
      >
        <IoMdClose size={24} color={"#ccc"} />
      </IconButton>

      {isLargerThan1080 || form === null ? (
        <>
          <Title style={{ textTransform: "uppercase" }}>
            <img
              src={"./icons/stop-card.svg"}
              alt="stop"
              css={css`
                margin-right: 4px;
              `}
            />
            Stop
          </Title>
          <TagsWrapper>{description}</TagsWrapper>
        </>
      ) : (
        <img src={"./icons/stop-card.svg"} alt="stop" />
      )}
    </Container>
  );
}
